'use client';

import { cn } from '@lib/util/cn';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductImageVM } from 'types/view-models';
import Thumbnail from '../thumbnail';

type ThumbnailsProps = {
  currentIndex: number;
  images: ProductImageVM[];
  onChange: (index: number) => void;
};

export default function Thumbnails({
  images,
  currentIndex,
  onChange,
}: ThumbnailsProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showGradient, setShowGradient] = useState(true);

  const checkScrollPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    setShowGradient(el.scrollHeight - el.scrollTop - el.clientHeight >= 8);
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(checkScrollPosition);
    return () => cancelAnimationFrame(frame);
  }, [checkScrollPosition]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const buttons = container.querySelectorAll<HTMLElement>('[data-thumbnail]');
    buttons[currentIndex]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [currentIndex]);

  return (
    <div className="relative desktop:max-h-[520px] medium:max-h-[620px] large:max-h-[660px]">
      <div
        ref={containerRef}
        onScroll={checkScrollPosition}
        className="no-scrollbar flex flex-col gap-4 medium:gap-5 overflow-y-scroll desktop:max-h-[520px] medium:max-h-[620px] large:max-h-[660px]"
      >
        {images.map((image, index) => (
          <Thumbnail
            as="button"
            type="button"
            data-thumbnail
            aria-current={index === currentIndex ? true : undefined}
            aria-label={`Photo ${index + 1} of ${images.length}`}
            className={cn(
              'w-[77px]! h-[77px]! medium:w-[97px]! medium:h-[97px]! shrink-0 border-2 shadow-none rounded-2 bg-[#F3F4F3]',
              index === currentIndex
                ? 'border-brand-accent-500'
                : 'border-transparent',
            )}
            imageClassName="mix-blend-multiply"
            key={image.url ?? index}
            onClick={() => onChange(index)}
            size="xsmall"
            sizes="(max-width: 1279px) 77px, 97px"
            thumbnail={image.url}
          />
        ))}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-linear-to-t from-white to-transparent transition-opacity duration-300',
          showGradient ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}
